import * as ReactDOM from 'react-dom/client';
import './i18n';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import React from 'react';
import * as Sentry from '@sentry/react';
import SplashScreen from './shared/components/SplashScreen';
import { GcalClient, GcalProvider } from '@geerd-nx/gcal';
import { GCAL } from './env';
import ErrorPage from './pages/Error/ErrorPage';

Sentry.init({
  dsn: 'https://d3b33f68bb25e904b06103be56550f7c@o4506373556600832.ingest.us.sentry.io/4507406893711360',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled

  tracePropagationTargets: [
    /^https:\/\/(\w+\.)?abs-elevate\.ma/,
    /^https:\/\/staging\.ocw-client\.pages\.dev/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: process.env?.['NODE_ENV'] ?? 'development',
  enabled: process.env?.['NODE_ENV'] !== 'development',
});

const AuthApp = React.lazy(() => import('./auth/App'));
const App = React.lazy(() => import('./app'));

const gcalClient = new GcalClient({
  ...GCAL,
  LoadingComponent: <SplashScreen />,
});
declare global {
  interface Window {
    Countly: any;
    hsConversationsSettings: Record<string, any>;
    hsConversationsOnReady: Array<() => void>;
    HubSpotConversations: {
      on: any;
      off: any;
      widget: {
        status: () => { loaded: boolean; pending: boolean };
        load: (params?: { widgetOpen: boolean }) => void;
        remove: () => void;
        open: () => void;
        close: () => void;
        refresh: (openToNewThread?: boolean) => void;
      };
    };
  }
}

// Prevent mouse scroll from changing number inputs values
document.addEventListener(
  'wheel',
  function (event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'number'
    ) {
      event.preventDefault();
    }
  },
  { passive: false }
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Sentry.ErrorBoundary fallback={<ErrorPage />}>
    <Router>
      <Routes>
        <Route
          path="/auth/*"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              <AuthApp />
            </React.Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <React.Suspense fallback={<SplashScreen />}>
              <GcalProvider client={gcalClient}>
                <App />
              </GcalProvider>
            </React.Suspense>
          }
        />
      </Routes>
    </Router>
  </Sentry.ErrorBoundary>
);
